import { useCallback, useEffect, useState } from 'react';
import { TenantId } from '@yourstore/domain';

/**
 * The console's addresses — hash routes, and nothing a server has to know.
 *
 * Until the redesign this console had no URLs on purpose: a pasted link was
 * argued to be "a cross-tenant screen in somebody's chat history". That
 * argument does not survive a second look (docs/console-redesign-plan.md §2).
 * Every read behind every screen is operator-authenticated and audited, so a
 * link opens nothing for anyone who could not already open it, and a tenant id
 * is a slug, not a secret. What the missing URL *did* cost was real: a refresh
 * threw the operator back to the fleet, the back button left the console, and
 * "look at Love Spa's Website tab" could not be said with a link.
 *
 * **Hash, not path**, because the console is static files on Hosting with one
 * rewrite; a hash never reaches the server, needs no rewrite rule, and cannot
 * 404 on a refresh.
 *
 * The table, in full:
 *
 *   #/                         the businesses (the fleet)
 *   #/new                      add a business (the onboarding wizard)
 *   #/b/<tenant_id>/<tab>      one business, on one tab
 *   #/b/<tenant_id>/site/editor  the page editor, on its own screen (B3; Phase C's builder)
 *   #/b/<tenant_id>/site/answers the owner's questionnaire answers, one guided screen (B3)
 *   #/b/<tenant_id>/catalog?item=<item_id>  what they sell, with one thing open (B4)
 *   #/log[?tenant=<id>]        who did what (the audit log), optionally one business's
 *   #/checks[?tenant=<id>]     checks & imports (the jobs), optionally about one business
 *
 * Anything else — a typo, an old bookmark, a tenant id that is not a slug — is
 * the businesses list, and the address bar is corrected to say so rather than
 * left showing an address the screen does not match.
 */

export const BUSINESS_TABS = [
  'overview',
  'setup',
  'site',
  'catalog',
  'connections',
  'activity',
] as const;
export type BusinessTab = (typeof BUSINESS_TABS)[number];

export type Route =
  | { kind: 'fleet' }
  | { kind: 'wizard' }
  | {
      kind: 'business';
      tenantId: string;
      tab: BusinessTab;
      editor?: boolean;
      answers?: boolean;
      /** The catalog's open item — `?item=` on the catalog tab only. */
      item?: string | undefined;
    }
  | { kind: 'log'; tenantId?: string | undefined }
  | { kind: 'checks'; tenantId?: string | undefined };

const FLEET: Route = { kind: 'fleet' };

/** What `?item=` may hold: an id's characters, bounded. */
const ITEM_ID = /^[A-Za-z0-9_-]{1,64}$/;

function isTab(value: string): value is BusinessTab {
  return (BUSINESS_TABS as readonly string[]).includes(value);
}

function tenantOf(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  return TenantId.safeParse(value).success ? value : undefined;
}

/**
 * The route an address means, or `null` when it means nothing. `null` is not
 * "the fleet": the caller needs to know the difference, because an unknown
 * address is corrected in the address bar and a known one is left alone.
 */
export function parseHash(hash: string): Route | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw === '' || raw === '/') return FLEET;

  const [pathPart = '', queryPart = ''] = raw.split('?', 2);
  const query = new URLSearchParams(queryPart);
  const parts = pathPart.split('/').filter((p) => p !== '');
  const segments = parts.map((p) => {
    try {
      return decodeURIComponent(p);
    } catch {
      return p;
    }
  });

  switch (segments[0]) {
    case 'new':
      return segments.length === 1 ? { kind: 'wizard' } : null;
    case 'log':
    case 'checks': {
      if (segments.length !== 1) return null;
      const asked = query.get('tenant');
      const tenantId = tenantOf(asked);
      // A tenant filter that is not a tenant id is an address that means
      // nothing, not a log for everyone — the operator asked for one business.
      if (asked !== null && tenantId === undefined) return null;
      return segments[0] === 'log' ? { kind: 'log', tenantId } : { kind: 'checks', tenantId };
    }
    case 'b': {
      const tenantId = tenantOf(segments[1]);
      if (tenantId === undefined) return null;
      if (segments.length === 2) return { kind: 'business', tenantId, tab: 'overview' };
      const tab = segments[2] ?? '';
      if (!isTab(tab)) return null;
      if (segments.length === 3 && tab === 'catalog') {
        // An item id is a prefixed ULID (or a fixture's slug); anything else is
        // an address that means the catalog with nothing chosen, not nothing.
        const item = query.get('item');
        return item !== null && ITEM_ID.test(item)
          ? { kind: 'business', tenantId, tab, item }
          : { kind: 'business', tenantId, tab };
      }
      if (segments.length === 3) return { kind: 'business', tenantId, tab };
      if (segments.length === 4 && tab === 'site' && segments[3] === 'editor') {
        return { kind: 'business', tenantId, tab, editor: true };
      }
      if (segments.length === 4 && tab === 'site' && segments[3] === 'answers') {
        return { kind: 'business', tenantId, tab, answers: true };
      }
      return null;
    }
    default:
      return null;
  }
}

/** The address of a route — the one spelling `parseHash` reads back to it. */
export function hrefOf(route: Route): string {
  switch (route.kind) {
    case 'fleet':
      return '#/';
    case 'wizard':
      return '#/new';
    case 'business':
      return `#/b/${encodeURIComponent(route.tenantId)}/${route.tab}${route.editor === true ? '/editor' : route.answers === true ? '/answers' : ''}${route.tab === 'catalog' && route.item !== undefined ? `?item=${encodeURIComponent(route.item)}` : ''}`;
    case 'log':
    case 'checks':
      return route.tenantId === undefined
        ? `#/${route.kind}`
        : `#/${route.kind}?tenant=${encodeURIComponent(route.tenantId)}`;
  }
}

export type Navigate = (route: Route, options?: { replace?: boolean }) => void;

function currentHash(): string {
  return typeof window === 'undefined' ? '' : window.location.hash;
}

/**
 * The route the address bar holds, and the way to move it.
 *
 * `navigate` **pushes** by default, so back and forward walk the operator's
 * own path; `replace` is for corrections (an unknown address, `#/b/<id>`
 * spelled without its tab) that should not leave a step behind them. The state
 * is set directly as well as through `hashchange`, so the screen changes on the
 * click rather than one event later, and a `hashchange` that arrives for the
 * same address is a no-op.
 */
export function useRoute(): [Route, Navigate] {
  const [route, setRoute] = useState<Route>(() => resolve(currentHash()).route);

  useEffect(() => {
    // The first address may itself need correcting — an old bookmark, a typo.
    const first = resolve(currentHash());
    if (first.correct) window.history.replaceState(null, '', hrefOf(first.route));

    const onChange = (): void => {
      const next = resolve(currentHash());
      if (next.correct) window.history.replaceState(null, '', hrefOf(next.route));
      setRoute(next.route);
    };
    // Both events: `hashchange` for an address typed or pasted, `popstate`
    // for Back and Forward across the entries `navigate` pushed (a traversal
    // between two pushed entries is not guaranteed to fire `hashchange`).
    window.addEventListener('hashchange', onChange);
    window.addEventListener('popstate', onChange);
    return () => {
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('popstate', onChange);
    };
  }, []);

  const navigate = useCallback<Navigate>((next, options = {}) => {
    const href = hrefOf(next);
    if (href !== currentHash()) {
      if (options.replace === true) window.history.replaceState(null, '', href);
      else window.history.pushState(null, '', href);
    }
    setRoute(next);
  }, []);

  return [route, navigate];
}

/**
 * The route for an address, and whether the address bar should be rewritten to
 * the route's own spelling — true for an address that means nothing (it becomes
 * `#/`) and for a business with no tab (it gains `/overview`).
 */
function resolve(hash: string): { route: Route; correct: boolean } {
  const parsed = parseHash(hash);
  if (parsed === null) return { route: FLEET, correct: true };
  const canonical = hrefOf(parsed);
  const spelled = hash === '' || hash === '#' ? '#/' : hash;
  return { route: parsed, correct: canonical !== spelled && parsed.kind === 'business' };
}
